import { Modal, Button, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';
import { AppModalCloseButton } from './AppModalCloseButton';

export type ConfirmDestructiveModalProps = {
  show: boolean;
  onHide: () => void;
  /** Called when user confirms; errors should be handled by the parent */
  onConfirm: () => void | Promise<void>;
  /** Shown below the irreversible warning */
  message: string;
  /** Modal title (defaults to common.delete_data_confirm_title) */
  title?: string;
  /** Extra content (e.g. highlighted entity name) */
  children?: ReactNode;
  isLoading?: boolean;
  confirmLabel?: string;
  cancelLabel?: string;
  /** 警告バナー文言（既定: common.delete_data_irreversible）。廃棄など動詞が違う操作で上書き */
  warningText?: string;
  /** 閉じるボタン title（既定: common.confirm_modal_close_title） */
  closeTitle?: string;
  /** キャンセル title（既定: common.confirm_modal_cancel_title） */
  cancelTitle?: string;
  /** 実行ボタン title（既定: common.confirm_modal_ok_title） */
  confirmTitle?: string;
};

/**
 * ごみ箱／削除系操作の共通確認モーダル（データ消失の警告・キャンセル・削除）
 */
export default function ConfirmDestructiveModal({
  show,
  onHide,
  onConfirm,
  message,
  title,
  children,
  isLoading = false,
  confirmLabel,
  cancelLabel,
  warningText,
  closeTitle,
  cancelTitle,
  confirmTitle,
}: ConfirmDestructiveModalProps) {
  const { t } = useTranslation();
  const resolvedCloseTitle =
    closeTitle ??
    t('common.confirm_modal_close_title', {
      defaultValue: '確認を閉じる',
    });
  const resolvedCancelTitle =
    cancelTitle ??
    t('common.confirm_modal_cancel_title', {
      defaultValue: '実行せずに閉じる',
    });
  const resolvedConfirmTitle =
    confirmTitle ??
    t('common.confirm_modal_ok_title', {
      defaultValue: 'この操作を実行する',
    });
  const resolvedConfirmLabel = confirmLabel ?? t('common.delete_confirm_ok');
  const resolvedCancelLabel = cancelLabel ?? t('common.cancel');
  const resolvedWarning = warningText ?? t('common.delete_data_irreversible');

  const handleConfirm = async () => {
    try {
      await onConfirm();
      onHide();
    } catch {
      // 親の mutation / onError で表示
    }
  };

  return (
    <Modal
      dialogClassName="sample-system-dialog"
      show={show}
      onHide={onHide}
      centered
      backdrop="static"
      keyboard={!isLoading}
    >
      <Modal.Header>
        <Modal.Title>{title ?? t('common.delete_data_confirm_title')}</Modal.Title>
        {!isLoading ? (
          <AppModalCloseButton
            onClick={onHide}
            ariaLabel={t('common.close')}
            title={resolvedCloseTitle}
          />
        ) : null}
      </Modal.Header>
      <Modal.Body>
        <Alert variant="warning" className="mb-3">
          <div className="fw-semibold mb-0">{resolvedWarning}</div>
        </Alert>
        <p className="mb-2">{message}</p>
        {children}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={onHide}
          disabled={isLoading}
          aria-label={resolvedCancelLabel}
          title={resolvedCancelTitle}
        >
          {resolvedCancelLabel}
        </Button>
        <Button
          variant="danger"
          onClick={handleConfirm}
          disabled={isLoading}
          aria-label={isLoading ? t('common.deleting') : resolvedConfirmLabel}
          title={isLoading ? t('common.deleting') : resolvedConfirmTitle}
        >
          {isLoading ? t('common.deleting') : resolvedConfirmLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
